/** The one certificate sentence (review must-fix 5; Leo 10-31 C1, C2, C8, C9, ruling 5): the rail composes
 * "{who}, {when}. {why}", the bar tooltip "{lead}: file the {ag} certificate {when}. {why}". On Jan 9-10 the server
 * sends an if-accepted step, and no line says file. */
import { rowDay } from "./shortDay";
import {
  CERT_AGENCY,
  FDNY_REVIEW_DAYS,
  JUDGMENT_STEP_DOB_CERT,
  JUDGMENT_STEP_DOB_IF_ACCEPTED,
  JUDGMENT_STEP_FDNY_IF_ACCEPTED,
  type ActionPlanFeed,
} from "./useActionPlanFeed";

export const ticketsWord = (n: number) =>
  n === 1 ? "this ticket" : `these ${n} tickets`;

const LATE: ReadonlySet<string> = new Set([
  JUDGMENT_STEP_FDNY_IF_ACCEPTED,
  JUDGMENT_STEP_DOB_IF_ACCEPTED,
]);
export const ifAccepted = (step: string | null | undefined): boolean =>
  !!step && LATE.has(step);

/** "needs a certificate first"; once late, "needs an accepted certificate". */
export const certNeed = (n: number, late: boolean) =>
  `${n === 1 ? "needs" : "need"} ${late ? "an accepted certificate" : "a certificate first"}`;

/** `when` is null once late: the sentence is `why` alone, and `rest` is the rail step's meta under a title that
 * already says the condition (Jan 9-10). */
export interface CertClause {
  when: string | null;
  why: string;
  rest?: string;
}

/** `by`: the rows' own date (sortBy). FDNY's Nov 9 is our date (Jan 8 less 60 days of review), DOB's Jan 8 is
 * acceptance. */
export const certClause = (
  step: string,
  n: number,
  by: string,
  feed: Pick<ActionPlanFeed, "today" | "amnesty">,
): CertClause => {
  const d = (iso: string) => rowDay(iso, feed.today);
  const one = n === 1;
  if (ifAccepted(step)) {
    const its = one ? "its" : "their";
    return {
      when: null,
      why: `If ${CERT_AGENCY[step]} accepted the certificate${one ? "" : "s"}, pay ${ticketsWord(n)} now at the amnesty price, in ${its} own checkout. If not, you owe the full balance.`,
      rest: `In ${its} own checkout. If not accepted, you owe the full balance.`,
    };
  }
  if (step === JUDGMENT_STEP_DOB_CERT)
    return {
      when: "now",
      why: `DOB must accept ${one ? "it" : "them"} by ${d(by)} for the amnesty price; DOB gives no review time.`,
    };
  const review = `FDNY says review can take up to ${FDNY_REVIEW_DAYS} days`;
  return by >= feed.today
    ? { when: `by ${d(by)}`, why: `${review}.` }
    : {
        when: "now",
        why: `${review}, so it may not finish by ${d(feed.amnesty?.endsBy ?? by)}.`,
      };
};

/** Ruling 5, through Jan 8: the rail's step 2 meta and its Judgment owed line. */
export const acceptedMeta = (ag: string) =>
  `Once ${ag} accepts the certificate`;
export const acceptedLine = (ag: string) =>
  `${acceptedMeta(ag)}, pay this ticket at the amnesty price in its own checkout.`;
